import xlsx from "xlsx";
import { TaskRevenuesDTO } from "../services/task.interface";

// 정산 엑셀 레이아웃: 콜렉터 1명 = 8행(업무 6종 + 추가수당 + 합계), 날짜 1개 = 차종 3컬럼

const WORK_TYPE_ROWS: ReadonlyArray<[string, string]> = [
  ["replaceReallocate", "교체/재배치"],
  ["replace", "교체"],
  ["allocate", "배치"],
  ["capture", "수거"],
  ["reallocate", "재배치"],
  ["footpadClean", "닦기"],
  ["extra", "추가수당"],
  ["total", "합계"],
];

const VEHICLE_INDEX: Record<string, number> = {
  SCOOTER: 0,
  BIKE: 1,
  MOPED: 2,
};

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function formatDate(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

// collect_res.worked_at 은 운영 작업일보다 하루 이르다.
// collector-be-api DateUtils.getCollectDate() 가 20시 컷오프로 날짜를 정하기 때문에
// 20시 이전 작업은 전날로 기록된다. 그래서 헤더 날짜 D 열은 worked_at D-1 을 매칭한다.
// 이 보정을 지우면 정산 파일의 작업일자가 하루 밀린다
function minusOneDay(dateStr: string): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() - 1);
  return formatDate(dt);
}

// Date(y, m, d) 숫자 생성자를 쓴다. new Date("yyyy-MM-dd") 는 UTC 로 파싱돼 KST 에서 하루 밀린다
function buildDateRange(startDate: string, endDate: string): string[] {
  const [sy, sm, sd] = startDate.split("-").map(Number);
  const [ey, em, ed] = endDate.split("-").map(Number);
  const cursor = new Date(sy, sm - 1, sd);
  const last = new Date(ey, em - 1, ed);
  const dates: string[] = [];
  while (cursor <= last) {
    dates.push(formatDate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
}

export function buildRevenueSheetData(
  revenues: TaskRevenuesDTO[],
  startDate: string,
  endDate: string
): { sheetData: any[][]; merges: xlsx.Range[] } {
  const headerDates = buildDateRange(startDate, endDate);
  const sheetData: any[][] = [];
  const merges: xlsx.Range[] = [];

  // 2단 헤더
  const headerRow0: any[] = [
    "기사",
    "소속",
    "구분",
    "업무",
    ...headerDates.flatMap((d) => [d, d, d]),
    "",
    "총합",
    "",
    "금액",
  ];
  const headerRow1: any[] = [
    "",
    "",
    "",
    "",
    ...headerDates.flatMap(() => ["킥보드", "자전거", "모페드"]),
    "킥보드",
    "자전거",
    "모페드",
  ];
  sheetData.push(headerRow0, headerRow1);

  // 날짜 헤더 가로 병합 (각 날짜 3컬럼)
  let headerColumn = 4;
  headerDates.forEach(() => {
    merges.push({
      s: { r: 0, c: headerColumn },
      e: { r: 0, c: headerColumn + 2 },
    });
    headerColumn += 3;
  });

  let row = 2; // 헤더 2행 다음부터 데이터 시작

  revenues.forEach((revenue) => {
    let totalPrice = 0;
    const collectorRows: any[][] = [];

    // 8행 초기화 (첫 행에만 기사/소속/구분 채움)
    WORK_TYPE_ROWS.forEach(([, label], idx) => {
      collectorRows[idx] = [
        idx === 0 ? revenue.collectorName : "",
        idx === 0 ? revenue.collectorBelonging : "",
        idx === 0 ? revenue.collectorType : "",
        label,
      ];
    });

    headerDates.forEach((headerDate, dateIdx) => {
      const works = new Map<string, number[]>();
      WORK_TYPE_ROWS.forEach(([key]) => works.set(key, [0, 0, 0]));

      // collect_res 는 (콜렉터, worked_at) 유일성 제약이 없어 같은 날 행이 둘 이상일 수 있다.
      // find 로 첫 건만 잡으면 나머지 작업량과 추가수당이 조용히 빠진다
      const workedAt = minusOneDay(headerDate);
      const details = revenue.details.filter((d) => d.date === workedAt);
      details.forEach((detail) => {
        detail.results.forEach((result) => {
          const vIdx = VEHICLE_INDEX[result.vehicleType];
          if (vIdx === undefined) return;
          result.works.forEach((work) => {
            const bucket = works.get(work.type);
            if (!bucket) return;
            bucket[vIdx] += work.quantity;
            totalPrice += work.totalPrice;
          });
        });

        detail.extras.forEach((extra) => {
          const price = extra.unitPrice * extra.quantity;
          works.get("extra")![0] += price;
          totalPrice += price;
        });
      });

      if (details.length > 0) {
        // 합계 = 닦기 포함 6개 업무 수량 합 (차종별)
        const sumKeys = [
          "replaceReallocate",
          "replace",
          "allocate",
          "capture",
          "reallocate",
          "footpadClean",
        ];
        const totalRow = works.get("total")!;
        for (let v = 0; v < 3; v++) {
          totalRow[v] = sumKeys.reduce(
            (acc, key) => acc + works.get(key)![v],
            0
          );
        }
      }

      // 추가수당 행: 해당 날짜 3컬럼 가로 병합
      merges.push({
        s: { r: row + 6, c: 4 + dateIdx * 3 },
        e: { r: row + 6, c: 6 + dateIdx * 3 },
      });

      WORK_TYPE_ROWS.forEach(([key], rowIdx) => {
        collectorRows[rowIdx].push(...works.get(key)!);
      });
    });

    // 행별 차종 총합 + 마지막(합계)행에 금액
    collectorRows.forEach((collectorRow, rowIdx) => {
      const totals = [0, 0, 0];
      for (let i = 4; i < collectorRow.length; i += 3) {
        totals[0] += collectorRow[i];
        totals[1] += collectorRow[i + 1];
        totals[2] += collectorRow[i + 2];
      }
      collectorRow.push(...totals);
      if (rowIdx === collectorRows.length - 1) {
        collectorRow.push(totalPrice);
      }
      sheetData.push(collectorRow);
    });

    // 기사/소속/구분 세로 병합 (8행)
    merges.push({ s: { r: row, c: 0 }, e: { r: row + 7, c: 0 } });
    merges.push({ s: { r: row, c: 1 }, e: { r: row + 7, c: 1 } });
    merges.push({ s: { r: row, c: 2 }, e: { r: row + 7, c: 2 } });

    row += 8;
  });

  return { sheetData, merges };
}

export function exportRevenuesToExcel(
  revenues: TaskRevenuesDTO[],
  startDate: string,
  endDate: string,
  fileName?: string
): void {
  const { sheetData, merges } = buildRevenueSheetData(
    revenues,
    startDate,
    endDate
  );

  const sheet = xlsx.utils.aoa_to_sheet(sheetData);
  sheet["!merges"] = merges;
  const book = xlsx.utils.book_new();
  xlsx.utils.book_append_sheet(book, sheet, "Sheet1");
  xlsx.writeFile(book, fileName ?? `정산_${startDate}_${endDate}.xlsx`);
}
